import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { Stack, Typography } from '@mui/material';
import { SUBSCRIPTION_STATUS } from '@/models/subscription';
import { getPriceById } from '@/utils/price';
import { useSelector } from 'react-redux';
import { useSubscribed } from '@/hooks';

function getMessageKey({ hasDispute, handlerPriceId, status }, prices) {
	const currentPrice = getPriceById(handlerPriceId, prices);

	const hasPayAction =
		status === SUBSCRIPTION_STATUS.PAST_DUE &&
		!!(currentPrice?.active || !handlerPriceId);

	if (hasDispute) {
		return 'billing.subscription.disputedPaymentMessage';
	}

	if (status === SUBSCRIPTION_STATUS.EXPIRED) {
		return 'billing.subscription.incompleteExpired';
	}

	if (status === SUBSCRIPTION_STATUS.CANCELED) {
		return 'billing.subscription.canceledSubscription';
	}

	if (status === SUBSCRIPTION_STATUS.PENDING || hasPayAction) {
		return 'billing.subscription.failedPaymentMessage';
	}
}

function Text(props) {
	return <Typography fontWeight={500} fontSize={12} {...props} />;
}

function Description({ subscriptionInfo }) {
	const { t } = useTranslation();
	const subscribed = useSubscribed();
	const {
		cancellationRequested,
		isCanceled,
		currentPeriodEnd,
		subscriptionEnded,
		status,
		trialingEndsAt
	} = subscriptionInfo;

	const prices = useSelector(state => state.checkout?.prices || []);

	const messageKey = getMessageKey(subscriptionInfo, prices);

	const willCancel = cancellationRequested && !isCanceled;

	if (messageKey) {
		return <Text>{t(messageKey)}</Text>;
	}

	const periodEnd =
		status === SUBSCRIPTION_STATUS.TRIALING
			? trialingEndsAt
			: currentPeriodEnd;

	const periodEndDate = dayjs.unix(periodEnd).format('LL');
	const subscriptionEndedDate = dayjs.unix(subscriptionEnded).format('LL');

	if (subscribed && willCancel) {
		return (
			<Stack spacing={1}>
				<Text>
					{t('billing.subscription.willCancel', {
						date: periodEndDate
					})}
				</Text>

				{subscriptionEnded && (
					<Text>
						{t('billing.subscription.canceledAt', {
							date: subscriptionEndedDate
						})}
					</Text>
				)}
			</Stack>
		);
	}

	if (subscribed && !willCancel) {
		return (
			<Stack spacing={1}>
				<Text>
					{t('billing.subscription.nextRenewal', {
						date: periodEndDate
					})}
				</Text>
			</Stack>
		);
	}

	return null;
}

export default Description;
